import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { ImportRowError, ImportJobStatus } from "@adva/types";
import { useCancelImportJob, useImportJob } from "@/hooks/use-import";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { IMPORT_STATUS_LABELS, formatEntityType } from "../labels";
import { groupErrorsByMessage, type ImportErrorGroup } from "../group-errors";

// ADV-938: statuses for which the cancel button is visible. Mirrors the
// service-layer CANCELLABLE_IMPORT_JOB_STATUSES set — keep these in sync.
const CANCELLABLE_STATUSES: ReadonlySet<ImportJobStatus> = new Set([
  "pending",
  "validating",
  "importing",
]);

interface JobDetailSheetProps {
  jobId: string | null;
  onClose: () => void;
}

export function JobDetailSheet({ jobId, onClose }: JobDetailSheetProps) {
  const { data, isLoading } = useImportJob(jobId);
  const job = data?.job;
  const [confirmCancel, setConfirmCancel] = useState(false);
  // The hook is always called with a string id; we only open the cancel
  // dialog when `job` exists, so the empty-string fallback is unreachable
  // in practice but keeps the hook order stable when no job is selected.
  const cancelMutation = useCancelImportJob(job?.id ?? "");
  const canCancel = job ? CANCELLABLE_STATUSES.has(job.status) : false;

  return (
    <Sheet
      open={jobId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Import job details</SheetTitle>
          <SheetDescription className="sr-only">
            Detail view for the selected import job
          </SheetDescription>
        </SheetHeader>

        {isLoading || !job ? (
          <div className="flex items-center justify-center py-12 text-[color:var(--color-text-lo)]">
            Loading…
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-medium">
                  {formatEntityType(job.entity_type)}
                </p>
                <p className="typo-body text-[color:var(--color-text-md)] truncate">
                  {job.source} · {job.mode}
                </p>
              </div>
              <StatusBadge
                status={job.status}
                label={IMPORT_STATUS_LABELS[job.status]}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Total Rows" value={job.total_rows} />
              <StatCard label="Created" value={job.created_count} />
              <StatCard label="Updated" value={job.updated_count} />
              <StatCard
                label="Errors"
                value={job.error_rows}
                variant={job.error_rows > 0 ? "danger" : undefined}
              />
            </div>

            {job.started_at && (
              <p className="typo-body text-[color:var(--color-text-md)]">
                Started {new Date(job.started_at).toLocaleString()}
                {job.completed_at && (
                  <>
                    {" · "}
                    Completed {new Date(job.completed_at).toLocaleString()}
                  </>
                )}
              </p>
            )}

            <ErrorsSection errors={job.errors} errorRows={job.error_rows} />

            {job.warnings.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">
                  Warnings ({job.warnings.length})
                </h3>
                <div className="max-h-40 overflow-y-auto rounded-md border border-[color:var(--color-border)]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[color:var(--color-border)] text-left">
                        <th className="px-3 py-1.5 typo-label text-[color:var(--color-text-md)]">
                          Row
                        </th>
                        <th className="px-3 py-1.5 typo-label text-[color:var(--color-text-md)]">
                          Message
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.warnings.map((w: ImportRowError, i: number) => (
                        <tr
                          key={i}
                          className="border-b border-[color:var(--color-border)] last:border-0"
                        >
                          <td className="px-3 py-1.5 typo-mono">{w.row}</td>
                          <td className="px-3 py-1.5 text-[color:var(--color-warning)]">
                            {w.message}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              {canCancel && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setConfirmCancel(true)}
                  disabled={cancelMutation.isPending}
                >
                  {cancelMutation.isPending ? "Cancelling…" : "Cancel import"}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </SheetContent>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this import?</AlertDialogTitle>
            <AlertDialogDescription>
              The worker stops at its next per-batch checkpoint. Rows that
              landed before cancellation stay in place. You can re-submit with
              mode=incremental to import the remaining rows safely — matching is
              done on external_id.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep running</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                cancelMutation.mutate(undefined, {
                  onSettled: () => setConfirmCancel(false),
                });
              }}
            >
              Cancel import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

function StatCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant?: "danger";
}) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] px-3 py-2">
      <p className="typo-label text-[color:var(--color-text-md)]">{label}</p>
      <p
        className={`text-lg font-semibold tabular-nums ${
          variant === "danger" && value > 0
            ? "text-[color:var(--color-danger)]"
            : "text-[color:var(--color-text-hi)]"
        }`}
      >
        {value.toLocaleString()}
      </p>
    </div>
  );
}

// ADV-1017: grouped-by-message errors view. Aggregate sentinel rows
// (`row < 0`) render in a dedicated callout above the grouped list, so
// whole-file failures aren't mixed in with per-row errors.
function ErrorsSection({
  errors,
  errorRows,
}: {
  errors: ImportRowError[];
  errorRows: number;
}) {
  if (errors.length === 0) return null;

  const aggregate = errors.filter((entry) => entry.row < 0);
  const groups = groupErrorsByMessage(errors);

  if (aggregate.length === 0 && groups.length === 0) return null;

  const [top, ...rest] = groups;
  const headingCount = errorRows > 0 ? errorRows : errors.length;

  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 text-[color:var(--color-danger)]" />
        Errors ({headingCount.toLocaleString()})
      </h3>

      {aggregate.length > 0 && (
        <div className="rounded-md border border-[color:var(--color-danger)]/30 bg-[color:var(--color-danger)]/5 p-3">
          <ul className="list-disc pl-5 typo-body text-[color:var(--color-danger)]">
            {aggregate.map((entry, i) => (
              <li key={i}>{entry.message}</li>
            ))}
          </ul>
        </div>
      )}

      {top && (
        <div className="rounded-md border border-[color:var(--color-danger)]/30 bg-[color:var(--color-danger)]/5 p-3">
          <p className="typo-label text-[color:var(--color-text-md)]">
            Most common reason
          </p>
          <p className="typo-body mt-0.5 text-[color:var(--color-text-hi)]">
            {top.message}
          </p>
          <p className="typo-label mt-1 text-[color:var(--color-text-md)]">
            {top.count.toLocaleString()} row{top.count === 1 ? "" : "s"}{" "}
            affected
          </p>
        </div>
      )}

      {rest.length > 0 && (
        <ul className="divide-y divide-[color:var(--color-border)] rounded-md border border-[color:var(--color-border)]">
          {rest.map((group) => (
            <ErrorGroupRow key={group.message} group={group} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ErrorGroupRow({ group }: { group: ImportErrorGroup }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-[color:var(--color-elevated)]"
      >
        <span className="typo-body truncate text-[color:var(--color-text-hi)]">
          {group.message}
        </span>
        <span className="typo-label shrink-0 text-[color:var(--color-text-md)]">
          {group.count.toLocaleString()} row{group.count === 1 ? "" : "s"}
        </span>
      </button>
      {open && (
        <ul className="border-t border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] px-3 py-2 typo-mono text-[color:var(--color-text-md)]">
          {group.rows.map((entry, i) => (
            <li key={`${entry.row}-${entry.field ?? ""}-${i}`}>
              row {entry.row}
              {entry.field ? ` · ${entry.field}` : ""}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
